import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronLeft, Minus, X } from "@digital-twins/design-system/icons";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Text } from "@/components/ui/Text";
import { assistantSessionsApi } from "@/lib/api/client";
import {
  onExperimentStatus,
  subscribeToExperiment,
  unsubscribeFromExperiment,
} from "@/lib/services/websocket";
import Link from "@/routing/Link";
import { ROUTES } from "@/routes/routes";
import type { ReactNode } from "react";
import type { QuickStudyDraftView, WorkflowRun } from "@digital-twins/shared-types";
import { runPaneRunStatusLabel } from "../ThreadRunPane/RunStepRow";
import type {
  AssistantLinkedStudy,
  AssistantLinkedRoom,
} from "@/lib/api/client";
import type { SessionSelection } from "./sessionState";
import {
  SESSION_SECTION_LABEL_KEYS,
  SESSION_SECTION_ORDER,
  isProgressRun,
  type SessionSectionId,
} from "./sessionSections";

interface SessionListState<T> {
  threadToken: string;
  items: Array<T>;
  failed: boolean;
}

interface SessionPaneProps {
  threadToken: string;
  selection: SessionSelection;
  onSelect: (selection: SessionSelection) => void;
  onMinimize: () => void;
  onHide: () => void;
  /** Opens a launched study in the pane, on its results. */
  onOpenStudy?: (studyToken: string) => void;
  /** A study that just launched: its row is briefly highlighted. */
  highlightStudyToken?: string | null;
  /**
   * Studies a quick study links to (saved origin/findings bindings). They
   * list only under Quick studies, never under Studies.
   */
  findingsStudyTokens?: ReadonlySet<string>;
  /** Replaces the list while a study frame is open. */
  studyFrame?: ReactNode;
  /** A12: a drill-in opened from the list (citation panel, expanded artifact). */
  detail?: ReactNode;
  /** Back from `detail` to the list. */
  onCloseDetail?: () => void;
  /** The live run, while something runs. Absent hides Progress. */
  progress?: ReactNode;
  /** The live run's token, so its history row is not listed twice. */
  activeRunToken?: string | null;
  /** Report-kind cards (reports, findings, study docs, digests). */
  reports?: ReactNode;
  /** Briefs, creatives, question sets, image stimuli. */
  artifacts?: ReactNode;
  /** Uploads, generated images, attached documents. */
  files?: ReactNode;
  /** Every source the thread's answers cited, the most cited first. */
  sources?: ReactNode;
  /** O1: the newest answer's calculations. */
  calculations?: ReactNode;
  /** A figure-chip click: each new nonce opens Calculations. */
  calculationsFocusNonce?: number;
  quickStudies?: Array<QuickStudyDraftView>;
  onOpenQuickStudy?: (draft: QuickStudyDraftView) => void;
  renderRun?: (run: WorkflowRun) => ReactNode;
  room?: ReactNode;
  refreshKey: string;
}

function quickStudyStatusKey(
  draft: QuickStudyDraftView,
):
  | "threads.quickStudy.ready"
  | "threads.sessions.failed"
  | "threads.sessions.cancelled"
  | "threads.sessions.fielding"
  | "threads.sessions.notRun"
  | "threads.sessions.preparing" {
  const status = draft.activeRun?.status;
  if (status === "completed") {
    return "threads.quickStudy.ready";
  }
  if (status === "failed") {
    return "threads.sessions.failed";
  }
  if (status === "cancelled" || draft.status === "cancelled") {
    return "threads.sessions.cancelled";
  }
  if (status === "running" || status === "pending") {
    return "threads.sessions.fielding";
  }
  // A1: a saved Quick Study a newer one replaced before it ran.
  if (!draft.activeRun && draft.status === "ready" && draft.canEdit === false) {
    return "threads.sessions.notRun";
  }
  return "threads.sessions.preparing";
}

interface SessionSectionProps {
  id: SessionSectionId;
  label: string;
  expanded: boolean;
  onToggle: () => void;
  children: ReactNode;
}

function SessionSection(props: SessionSectionProps) {
  const { id, label, expanded, onToggle, children } = props;

  return (
    <section
      className="border-b border-border/50 px-3 last:border-b-0"
      data-testid={`thread-session-section-${id}`}
    >
      <button
        type="button"
        className="group flex w-full cursor-pointer items-center justify-between gap-2 px-1 py-2.5 text-left"
        data-testid={`thread-session-toggle-${id}`}
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <Text
          as="span"
          size="sm"
          weight="medium"
          className="transition-colors group-hover:text-foreground"
        >
          {label}
        </Text>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-[transform,color] group-hover:text-foreground",
            expanded ? "rotate-180" : "",
          )}
          aria-hidden
        />
      </button>
      {expanded ? <div className="flex flex-col pb-3">{children}</div> : null}
    </section>
  );
}

/**
 * The thread's side pane: one list, no tabs (A12). Sections in the owner's
 * order, each only when non-empty; an item opens as a drill-in with Back.
 * Server links own the studies, chats and run history; status events
 * invalidate their REST projection.
 */
export function SessionPane(props: SessionPaneProps) {
  const {
    threadToken,
    selection,
    onSelect,
    onMinimize,
    onHide,
    onOpenStudy,
    highlightStudyToken,
    findingsStudyTokens,
    studyFrame,
    detail,
    onCloseDetail,
    progress,
    activeRunToken,
    reports,
    artifacts,
    files,
    sources,
    calculations,
    calculationsFocusNonce,
    quickStudies = [],
    onOpenQuickStudy,
    renderRun,
    room,
    refreshKey,
  } = props;
  const { t } = useTranslation("app");
  const [studyState, setStudyState] = useState<
    SessionListState<AssistantLinkedStudy>
  >({ threadToken, items: [], failed: false });
  const [roomState, setRoomState] = useState<
    SessionListState<AssistantLinkedRoom>
  >({ threadToken, items: [], failed: false });
  const [runState, setRunState] = useState<SessionListState<WorkflowRun>>({
    threadToken,
    items: [],
    failed: false,
  });
  const [refresh, setRefresh] = useState(0);
  const [collapsed, setCollapsed] = useState<Set<SessionSectionId>>(
    () => new Set(),
  );
  const studyRequest = useRef(0);
  const studies =
    studyState.threadToken === threadToken ? studyState.items : [];
  const rooms = roomState.threadToken === threadToken ? roomState.items : [];
  const runHistory = runState.threadToken === threadToken ? runState.items : [];
  const studiesFailed = studyState.threadToken === threadToken && studyState.failed;
  const roomsFailed = roomState.threadToken === threadToken && roomState.failed;
  const runsFailed = runState.threadToken === threadToken && runState.failed;
  const studyTokens = studies.map((study) => study.token).join(",");

  useEffect(() => {
    let cancelled = false;
    const studyVersion = ++studyRequest.current;
    void Promise.allSettled([
      assistantSessionsApi.studies(threadToken),
      assistantSessionsApi.rooms(threadToken),
      assistantSessionsApi.runs(threadToken),
    ]).then(([nextStudies, nextRooms, nextRuns]) => {
      if (cancelled) {
        return;
      }
      if (studyVersion === studyRequest.current) {
        setStudyState({
          threadToken,
          items: nextStudies.status === "fulfilled" ? nextStudies.value : [],
          failed: nextStudies.status === "rejected",
        });
      }
      setRoomState({
        threadToken,
        items: nextRooms.status === "fulfilled" ? nextRooms.value : [],
        failed: nextRooms.status === "rejected",
      });
      setRunState({
        threadToken,
        items: nextRuns.status === "fulfilled" ? nextRuns.value : [],
        failed: nextRuns.status === "rejected",
      });
    });
    return () => {
      cancelled = true;
    };
  }, [threadToken, refreshKey, refresh]);

  useEffect(() => {
    let cancelled = false;
    const tokens = studyTokens ? studyTokens.split(",") : [];
    const refreshStudies = () => {
      const version = ++studyRequest.current;
      void assistantSessionsApi
        .studies(threadToken)
        .then((items) => {
          if (!cancelled && version === studyRequest.current) {
            setStudyState({ threadToken, items, failed: false });
          }
        })
        .catch(() => {
          if (!cancelled && version === studyRequest.current) {
            setStudyState((current) => ({ ...current, failed: true }));
          }
        });
    };
    const off = tokens.map((token) => {
      const unsubscribe = onExperimentStatus(token, refreshStudies);
      subscribeToExperiment(token);
      return () => {
        unsubscribe();
        unsubscribeFromExperiment(token);
      };
    });
    return () => {
      cancelled = true;
      off.forEach((dispose) => dispose());
    };
  }, [studyTokens, threadToken]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const refreshOnFocus = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setRefresh((value) => value + 1), 250);
    };
    window.addEventListener("focus", refreshOnFocus);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("focus", refreshOnFocus);
    };
  }, []);

  useEffect(() => {
    if (calculationsFocusNonce === undefined) {
      return;
    }
    setCollapsed((current) => {
      if (!current.has("calculations")) {
        return current;
      }
      const next = new Set(current);
      next.delete("calculations");
      return next;
    });
  }, [calculationsFocusNonce]);

  if ((selection.kind === "study" || selection.kind === "quick_study") && studyFrame) {
    return (
      <div
        className="flex min-h-0 flex-1 flex-col"
        data-testid="thread-session-pane"
      >
        {studyFrame}
      </div>
    );
  }

  const toggleSection = (id: SessionSectionId) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const retry = () => setRefresh((value) => value + 1);

  const failedLine = (id: SessionSectionId) => (
    <div className="flex items-center gap-2 px-1 py-1" role="alert">
      <Text size="sm" color="muted">
        {t("threads.sessions.loadFailed")}
      </Text>
      <Button
        variant="ghost"
        size="sm"
        data-testid={`thread-session-retry-${id}`}
        onClick={retry}
      >
        {t("threads.sessions.retry")}
      </Button>
    </div>
  );

  const runRow = (run: WorkflowRun) => (
    <Button
      key={run.token}
      variant="ghost"
      className="shrink-0 justify-between gap-2"
      data-testid={`thread-session-run-${run.token}`}
      onClick={() => onSelect({ kind: "runs", token: run.token })}
    >
      <span className="truncate">{run.templateDisplayName}</span>
      <span className="text-xs text-muted-foreground">
        {runPaneRunStatusLabel(run.status)}
      </span>
    </Button>
  );

  const pastRuns = runHistory.filter((run) => run.token !== activeRunToken);
  const progressRuns = pastRuns.filter(isProgressRun);
  const finishedRuns = pastRuns.filter((run) => !isProgressRun(run));
  const listedStudies = studies.filter(
    (study) => !findingsStudyTokens?.has(study.token),
  );

  const studyRow = (study: AssistantLinkedStudy) => (
    <div
      key={study.token}
      className={cn(
        "flex shrink-0 flex-col gap-2 rounded-md px-1 py-2 transition-colors duration-700 motion-reduce:transition-none",
        study.token === highlightStudyToken &&
          "bg-primary/10 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-1 motion-safe:duration-300",
      )}
      data-testid={`thread-session-study-${study.token}`}
      data-highlighted={study.token === highlightStudyToken ? "true" : undefined}
    >
      <Text size="sm">{study.title}</Text>
      <Text size="xs" color="muted">
        {t(
          `threads.sessions.${study.reportStatus === "failed" || study.status === "failed" ? "failed" : study.status === "cancelled" ? "cancelled" : study.reportStatus === "ready" ? "ready" : study.status === "completed" ? "analysing" : study.status === "running" ? "fielding" : "preparing"}`,
        )}
      </Text>
      {study.reportStatus === "ready" ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            data-testid={`thread-session-study-report-${study.token}`}
            onClick={() => onOpenStudy?.(study.token)}
          >
            {t("threads.sessions.openReport")}
          </Button>
          <Button variant="ghost" size="sm" asChild>
            <Link
              href={ROUTES.studyResults(study.token)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`thread-session-study-new-tab-${study.token}`}
            >
              {t("threads.sessions.openInNewTab")}
            </Link>
          </Button>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          disabled
          data-testid={`thread-session-study-report-${study.token}`}
        >
          {t("threads.sessions.openReport")}
        </Button>
      )}
    </div>
  );

  const sectionContent: Record<SessionSectionId, ReactNode> = {
    progress:
      progress || progressRuns.length ? (
        <>
          {progress}
          {progressRuns.map(runRow)}
        </>
      ) : null,
    reports:
      reports || finishedRuns.length || runsFailed ? (
        <>
          {runsFailed ? failedLine("reports") : null}
          {finishedRuns.map(runRow)}
          {reports}
        </>
      ) : null,
    "quick-studies": quickStudies.length ? (
      <>
        {quickStudies.map((draft) => {
          const isReady = draft.activeRun?.status === "completed";
          const status = t(quickStudyStatusKey(draft));
          return (
            <div key={draft.draftToken} className="flex shrink-0 items-center gap-2 px-1 py-1">
              {/* The title is a label, aligned with the other sections; View is the only way in. */}
              <Text
                size="sm"
                className="min-w-0 flex-1 truncate"
                data-testid={`thread-session-quick-study-${draft.draftToken}`}
                title={status}
              >
                {draft.title}
              </Text>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                disabled={!isReady}
                title={isReady ? undefined : status}
                aria-label={t("threads.sessions.viewNamed", { name: draft.title })}
                data-testid={`thread-session-quick-study-view-${draft.draftToken}`}
                onClick={() => onOpenQuickStudy?.(draft)}
              >
                {t("threads.sessions.view")}
              </Button>
            </div>
          );
        })}
      </>
    ) : null,
    studies:
      listedStudies.length || studiesFailed ? (
        <>
          {studiesFailed ? failedLine("studies") : null}
          {listedStudies.map(studyRow)}
        </>
      ) : null,
    chats:
      rooms.length || roomsFailed ? (
        <>
          {roomsFailed ? failedLine("chats") : null}
          {rooms.map((item) => (
            <Button
              key={item.token}
              variant="ghost"
              className="shrink-0 justify-start"
              data-testid={`thread-session-room-${item.token}`}
              onClick={() => onSelect({ kind: "rooms", token: item.token })}
            >
              {item.title || t("threads.sessions.sections.chats")}
            </Button>
          ))}
        </>
      ) : null,
    artifacts: artifacts || null,
    files: files || null,
    sources: sources || null,
    calculations: calculations || null,
  };

  const selectedRun =
    selection.kind === "runs" && selection.token
      ? runHistory.find((run) => run.token === selection.token)
      : undefined;
  const drillIn: { node: ReactNode; onBack: () => void } | null = detail
    ? { node: detail, onBack: () => onCloseDetail?.() }
    : selectedRun && renderRun
      ? {
          node: renderRun(selectedRun),
          onBack: () => onSelect({ kind: "runs" }),
        }
      : selection.kind === "rooms" && selection.token && room
        ? { node: room, onBack: () => onSelect({ kind: "rooms" }) }
        : null;

  const visibleSections = SESSION_SECTION_ORDER.filter(
    (id) => sectionContent[id] !== null,
  );

  return (
    <div
      className="flex min-h-0 flex-1 flex-col motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200"
      data-testid="thread-session-pane"
    >
      <div className="flex shrink-0 items-center gap-1 border-b border-border p-2">
        {drillIn ? (
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 px-2 text-xs text-muted-foreground"
            data-testid="thread-session-back"
            onClick={drillIn.onBack}
          >
            <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
            {t("threads.sessions.backToSessions")}
          </Button>
        ) : null}
        <div className="min-w-0 flex-1" />
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          data-testid="thread-session-minimize"
          aria-label={t("threads.sessions.minimize")}
          onClick={onMinimize}
        >
          <Minus className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          data-testid="thread-session-hide"
          aria-label={t("threads.sessions.hide")}
          onClick={onHide}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div
        className="flex min-h-0 flex-1 flex-col overflow-y-auto"
        data-testid="thread-session-list"
      >
        {drillIn ? (
          drillIn.node
        ) : visibleSections.length === 0 ? (
          <Text
            size="sm"
            color="muted"
            className="p-4"
            data-testid="thread-session-empty"
          >
            {t("threads.sessions.empty")}
          </Text>
        ) : (
          visibleSections.map((id) => (
            <SessionSection
              key={id}
              id={id}
              label={t(SESSION_SECTION_LABEL_KEYS[id])}
              expanded={!collapsed.has(id)}
              onToggle={() => toggleSection(id)}
            >
              {sectionContent[id]}
            </SessionSection>
          ))
        )}
      </div>
    </div>
  );
}
